import type { ExploratoryTestStatus } from '../exploratory-tests/types';

export type TicketRun = {
  id: string;
  status: ExploratoryTestStatus;
  createdAt: string;
  finishedAt: string | null;
};

export type TicketListItem = {
  identifier: string;
  title: string;
  url: string;
  status: string;
  updatedAt: string | null;
  run: TicketRun | null;
};

/** `label: null` means no tag is configured yet. */
export type TicketsResponse = {
  ok: true;
  data: { label: string | null; limit: number; tickets: TicketListItem[] };
};

export type TicketSettings = {
  defaultProjectKey: string | null;
  tagTriggerLabel: string | null;
  tagTriggerPropertyId: string | null;
  tagTriggerEnvironmentId: string | null;
  tagTriggerInstructions: string | null;
  ticketRunAllowMapUpdates: boolean;
};

export type TicketSettingsResponse = { ok: true; data: TicketSettings };

/** Send `ticketRunAllowMapUpdates` only when an admin changed it. */
export type TicketSettingsUpdate = {
  tagTriggerLabel: string;
  tagTriggerPropertyId: string | null;
  tagTriggerEnvironmentId: string | null;
  tagTriggerInstructions: string | null;
  ticketRunAllowMapUpdates?: boolean;
};

export type TicketEnvironmentOption = {
  id: string;
  name: string;
  type: 'permanent' | 'ephemeral' | 'local' | 'sandbox';
  isDefault: boolean;
  url: string | null;
  sandboxV2Environment: string | null;
};

export const TICKET_INSTRUCTIONS_MAX_LENGTH = 2000;

/** Tag prefilled the first time tag-triggered tests are set up. */
export const DEFAULT_TICKET_TAG = 'cofactor-test';

/** Permanent environments and Sandboxes v2 definitions; not ephemeral instances, sandbox v1, or local. */
// Keep in step with isTagTriggerEnvironment (apps/api/src/integrations/ticketing/tag-trigger.ts): the server enforces this rule on save and on dispatch.
export const isSelectableTicketEnvironment = (environment: TicketEnvironmentOption) =>
  environment.type === 'permanent' ||
  (environment.type === 'sandbox' && environment.sandboxV2Environment !== null);

/** Dropdown and summary label for an environment; keeps the two in agreement. */
export const ticketEnvironmentLabel = (environment: TicketEnvironmentOption): string =>
  environment.sandboxV2Environment
    ? `${environment.name} (sandbox)`
    : environment.isDefault
      ? `${environment.name} (default)`
      : environment.name;
